<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import { useFrameBridge } from './useFrameBridge';
import type { ParentBridge } from '@monorepo/aichat-framebridge';

interface Props {
  src: string;
  onClose?: (refresh?: boolean) => void;
  onBridgeReady?: (bridge: ParentBridge) => void;
}

const { src, onClose, onBridgeReady } = defineProps<Props>();
const loaded = ref(false);

const { iframeRef, init } = useFrameBridge(src);

onMounted(async () => {
  await nextTick();
  if (iframeRef.value) {
    const bridge = init(iframeRef.value);
    bridge?.on('agent:close', (refresh?: boolean) => {
      onClose?.(refresh);
    });
    bridge && onBridgeReady?.(bridge);
  }
});

const onLoad = async () => {
  loaded.value = true;
};
</script>

<template>
  <div class="iframe-container">
    <iframe ref="iframeRef" :src="src" :onload="onLoad" />
    <div v-if="!loaded" class="agent-add-iframe-loading">
      <h3></h3>
      <ul>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.iframe-container {
  position: relative;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
  }

  .agent-add-iframe-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #fff;
    padding: 2rem;
    z-index: 1;

    --skeleton-gradient-from-color: rgba(0, 0, 0, 0.06);
    --skeleton-gradient-to-color: rgba(0, 0, 0, 0.15);

    h3,
    li {
      margin-bottom: 16px;
      height: 16px;
      background: linear-gradient(
        90deg,
        var(--skeleton-gradient-from-color) 25%,
        var(--skeleton-gradient-to-color) 37%,
        var(--skeleton-gradient-from-color) 63%
      );
      background-size: 400% 100%;
      animation-name: skeleton-loading;
      animation-duration: 1.4s;
      animation-timing-function: ease;
      animation-iteration-count: infinite;
      border-radius: 2px;
    }

    h3 {
      width: 38%;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        margin-bottom: 16px;
      }
      li:last-child {
        margin-bottom: 0;
        width: 61%;
      }
    }
  }

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
}
</style>
